import { useEffect, useState } from "react";
import { Center, Loader } from "@mantine/core";
import { httpSource, setDataSource } from "../data";
import { useAuthStore, useIsAuthed } from "../stores/authStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { useConsoleStore } from "../stores/consoleStore";
import { useQueryStore } from "../stores/queryStore";
import { BaseProvider } from "../components/ConsoleShell";
import ConsoleRoutes from "./ConsoleRoutes";
import SignIn from "../pages/SignIn";

/** The real console: HTTP data source, Firebase auth, no dev toolbar. */
export default function LiveRoot() {
  const authed = useIsAuthed();
  const loading = useAuthStore((s) => s.loading);
  const devSession = useAuthStore((s) => s.devSession);
  const exitDevMode = useAuthStore((s) => s.exitDevMode);
  // The uid, not the User object: identity is what invalidates cached rows, and
  // Firebase hands back a new object reference on every token refresh.
  const uid = useAuthStore((s) => s.user?.uid ?? null);
  const loadWorkspaces = useWorkspaceStore((s) => s.load);
  const resetConsole = useConsoleStore((s) => s.resetAll);
  const resetQuery = useQueryStore((s) => s.reset);
  const resetWorkspaces = useWorkspaceStore((s) => s.reset);
  const [ready, setReady] = useState(false);
  // Which identity the caches have already been cleared for. `undefined` means
  // "not yet reset for anyone", which is distinct from the signed-out `null`.
  const [sessionUid, setSessionUid] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    setDataSource(httpSource);
    // Navigating out of /dev must not leave the fake session behind.
    if (devSession) exitDevMode();
    setReady(true);
  }, [devSession, exitDevMode]);

  // Sign-in, sign-out and account switch all clear every cached collection, so
  // one tenant's rows can never be rendered under another's session.
  //
  // Recording the identity we reset FOR is load-bearing, not bookkeeping. React
  // runs child effects before parent effects, so without this the screen mounted
  // on first render would fire its loaders, then this effect would bump the
  // store epoch and the in-flight responses would be dropped as stale. A screen
  // whose loader is only triggered on mount (its effect keyed on
  // `items.length === 0`) then never refetches and hangs on its loading state
  // forever. Gating the subtree below on sessionUid === uid means no child
  // fetches until the reset for that identity has already happened.
  useEffect(() => {
    resetConsole();
    resetQuery();
    resetWorkspaces();
    setSessionUid(uid);
  }, [uid, resetConsole, resetQuery, resetWorkspaces]);

  useEffect(() => {
    if (ready && authed) void loadWorkspaces();
  }, [ready, authed, uid, loadWorkspaces]);

  if (loading || !ready) {
    return (
      <Center h="100vh" bg="var(--vf-bg)">
        <Loader color="var(--vf-acc)" />
      </Center>
    );
  }

  if (!authed) return <SignIn />;

  // Authed, but the caches still belong to the previous identity — hold the
  // subtree back for the one render it takes the reset effect to catch up.
  if (sessionUid !== uid) {
    return (
      <Center h="100vh" bg="var(--vf-bg)">
        <Loader color="var(--vf-acc)" />
      </Center>
    );
  }

  return (
    <BaseProvider base="">
      {/* Keyed by identity: an account switch remounts every screen, so
          mount-triggered loaders re-run for the new tenant instead of showing
          the previous one's emptied caches. */}
      <ConsoleRoutes key={uid ?? "anon"} />
    </BaseProvider>
  );
}
